import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import pDebounce from 'p-debounce'
import { useTranslation } from 'react-i18next'
import Popper from '@mui/material/Popper'
import { isEmpty } from 'lodash'
import MuiAutoComplete, {
  CATEGORY,
} from 'ggx-componentlibrary/components/autocomplete/mui-autocomplete/mui-autocomplete.component'
import { AddressBookIcon } from 'ggx-componentlibrary/components/autocomplete/mui-autocomplete/Options/options.component.styles'
import {
  useDefaultOptionsWithoutRecentAddress,
  buildAddressBookOptions,
  buildRecentAddressOptions,
  FIELD_NAME,
} from './utils'

const NameAutocompleteV2 = ({
  className,
  name = {},
  address = {},
  latitude = {},
  longitude = {},
  dataTestid,
  placeholder,
  onChange,
  onOpenAddressBookModal,
  onBlur,
  onHover,
  onInputChange,
  isAddressBookOptionsReady,
}) => {
  const [t] = useTranslation()
  const { touched, error } = name.meta || {}
  const { value: nameValue } = name.input || {}
  const autocompleteValue = { label: nameValue, value: nameValue }

  const handleAutocompleteQuery = useMemo(() => {
    const debouncedPreparedOptions = pDebounce(
      value =>
        buildAddressBookOptions(t)(FIELD_NAME.NAME, value).catch(err => {
          console.warn(
            'NameAutocomplete getContacts failed => searchText: ',
            value
          )
          console.warn('NameAutocomplete getContacts failed => err: ', err)
        }),
      500
    )
    return async value => await debouncedPreparedOptions(value)
  }, [t])

  const handleAutocompleteChange = useCallback(
    async (valueObj = {}) => {
      if (!address?.input || !latitude?.input || !longitude?.input) return

      if (isEmpty(valueObj))
        return onChange({ address: null, latitude: null, longitude: null })

      const value =
        (valueObj.groupType === 'recent-address' && valueObj) ||
        (valueObj.groupType === 'saved-address-book' && valueObj.value) ||
        null
      if (value) {
        onChange({
          address: value.address,
          latitude: value.latitude.toString(),
          longitude: value.longitude.toString(),
          details: value.detail_address,
          phone_number: value.contact_phone_number,
          name: value.contact_name,
          saved_address_id: value.saved_address_id,
        })
      }
    },
    [address, latitude, longitude]
  )

  const defaultOptions = useDefaultOptionsWithoutRecentAddress(
    FIELD_NAME.NAME,
    nameValue,
    isAddressBookOptionsReady
  )

  return (
    <MuiAutoComplete
      className={className}
      category={CATEGORY.GENERIC}
      onInputChange={value => {
        onInputChange()
        name.input?.onChange(value)
      }}
      onBlur={() => {
        onBlur()
        name.input?.onBlur(nameValue)
      }}
      onHover={onHover}
      onQuery={handleAutocompleteQuery}
      onChange={handleAutocompleteChange}
      defaultOptions={defaultOptions}
      menuHeight={300}
      meta={{ touched, error }}
      showSaveAddressModal={onOpenAddressBookModal}
      selectedOption={autocompleteValue}
      supportEditSelectedValue
      placeholder={placeholder}
      dataTestid={dataTestid}
      customPopperComponent={({ style, ...props }) => (
        <Popper
          style={{ width: '453px' }}
          {...props}
          placement="bottom-start"
          sx={{
            zIndex: '100 !important',
          }}
        />
      )}
      endAdornment={
        <AddressBookIcon
          onClick={() => onOpenAddressBookModal('pick-from-address-book')}
        />
      }
    />
  )
}

const reduxFormPropTypeShape = PropTypes.shape({
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
})

NameAutocompleteV2.propTypes = {
  className: PropTypes.string,
  name: reduxFormPropTypeShape,
  address: reduxFormPropTypeShape,
  latitude: reduxFormPropTypeShape,
  longitude: reduxFormPropTypeShape,
  dataTestid: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onOpenAddressBookModal: PropTypes.func,
  onBlur: PropTypes.func,
  onHover: PropTypes.func,
  onInputChange: PropTypes.func,
  isAddressBookOptionsReady: PropTypes.bool,
}

export { NameAutocompleteV2 }
